import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Music, TrendingUp, Clock, Sparkles, Volume2, Play } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Genre color mapping for visual variety
const GENRE_COLORS = {
  "Melodic Techno": "text-purple-400",
  "Deep House": "text-blue-400",
  "Techno": "text-red-400",
  "House": "text-green-400",
  "Minimal": "text-gray-400",
  "Progressive": "text-cyan-400",
  "Electronic": "text-primary",
  "Electronica": "text-yellow-400"
};

const getGenreColor = (genre) => {
  for (const [key, color] of Object.entries(GENRE_COLORS)) {
    if (genre?.toLowerCase().includes(key.toLowerCase())) {
      return color;
    }
  }
  return "text-primary";
};

// Mini recommendation card
const RecommendationCard = ({ event }) => {
  const hasAudio = event.track_url || event.audio;
  
  return (
    <Link 
      to={`/events/${event.event_id}`}
      className="flex items-center gap-3 p-3 rounded-lg bg-background/50 hover:bg-background/80 transition-all border border-border/50 hover:border-primary/30 group"
      data-testid={`recommendation-${event.event_id}`}
    >
      {/* Event Image */}
      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 relative">
        <img 
          src={event.image_url || "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=100"} 
          alt={event.name}
          className="w-full h-full object-cover"
        />
        {hasAudio && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Play className="w-4 h-4 text-primary" />
          </div>
        )}
      </div>
      
      {/* Event Info */}
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-foreground line-clamp-1 group-hover:text-primary transition-colors">
          {event.name}
        </h4>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[10px] font-mono uppercase ${getGenreColor(event.genre)}`}>
            {event.genre}
          </span>
          {hasAudio && <Volume2 className="w-3 h-3 text-primary" />}
        </div>
      </div>
      
      {/* Relevance indicator */}
      {event.relevance_score > 0 && (
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <Sparkles className="w-3 h-3 text-primary" />
          <span>{Math.round(event.relevance_score)}%</span>
        </div>
      )}
    </Link>
  );
};

// Recent play item
const RecentPlayItem = ({ play }) => {
  return (
    <div className="flex items-center gap-2 py-2 px-3 rounded bg-muted/20">
      <Volume2 className={`w-3 h-3 ${getGenreColor(play.genre)}`} />
      <span className="text-xs text-foreground line-clamp-1 flex-1">{play.event_name}</span>
      <span className={`text-[10px] font-mono uppercase ${getGenreColor(play.genre)}`}>
        {play.genre?.split(' ')[0]}
      </span>
    </div>
  );
};

const SoundProfile = () => {
  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSoundProfile = async () => {
      try {
        // Fetch stats and recommendations in parallel
        const [statsRes, recsRes] = await Promise.all([
          fetch(`${API_URL}/api/sound-profile/stats`, { credentials: 'include' }),
          fetch(`${API_URL}/api/sound-profile/recommendations?limit=5`, { credentials: 'include' })
        ]);
        
        const statsData = await statsRes.json();
        const recsData = await recsRes.json();
        
        setStats(statsData);
        setRecommendations(recsData.recommendations || []);
      } catch (error) {
        console.error('Error fetching sound profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSoundProfile();
  }, []);

  if (loading) {
    return (
      <div className="glass rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-muted/30 rounded w-32 mb-4"></div>
        <div className="space-y-3">
          <div className="h-16 bg-muted/20 rounded"></div>
          <div className="h-16 bg-muted/20 rounded"></div>
        </div>
      </div>
    );
  }

  const hasHistory = stats?.total_plays > 0;

  return (
    <div className="glass rounded-xl overflow-hidden" data-testid="sound-profile-section">
      {/* Header */}
      <div className="p-4 border-b border-border/50 bg-gradient-to-r from-primary/10 to-transparent">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Music className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-bold text-foreground">Your Vibe</h3>
          </div>
          {hasHistory && (
            <span className="text-xs text-muted-foreground font-mono">
              {stats.total_plays} plays
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {hasHistory ? (
          <>
            {/* Genre Breakdown */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-secondary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Top Genres</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.genre_breakdown || {}).slice(0, 4).map(([genre, count]) => (
                  <span 
                    key={genre}
                    className={`px-2 py-1 rounded-full text-[10px] font-mono uppercase bg-muted/30 ${getGenreColor(genre)}`}
                  >
                    {genre} ({count})
                  </span>
                ))}
              </div>
            </div>

            {/* Recent Plays */}
            {stats.recent_plays?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-accent" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Recent Plays</span>
                </div>
                <div className="space-y-1">
                  {stats.recent_plays.slice(0, 3).map((play, i) => (
                    <RecentPlayItem key={i} play={play} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-4">
            <Volume2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Play some event previews to build your sound profile
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              We'll recommend events based on your taste
            </p>
          </div>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                {hasHistory ? "For You" : "Guy J Approved"}
              </span>
            </div>
            <div className="space-y-2">
              {recommendations.map((event) => (
                <RecommendationCard key={event.event_id} event={event} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoundProfile;
